import { useMutation } from '@apollo/client';
import type { UpdateDocumentAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateDocumentAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateDocumentAssessmentResultTRPCInput } from './useUpdateDocumentAssessmentResultTRPC';
import { useUpdateDocumentAssessmentResultTRPC } from './useUpdateDocumentAssessmentResultTRPC';

export const useUpdateDocumentAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateDocumentAssessmentResultGraphQL, graphqlState] = useMutation(
    UpdateDocumentAssessmentResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'document_assessment_result_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateDocumentAssessmentResultTRPC();

  const updateDocumentAssessmentResult = async (
    variables: UpdateDocumentAssessmentResultTRPCInput
  ): Promise<UpdateDocumentAssessmentResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateDocumentAssessmentResult(variables);
    }

    const result = await updateDocumentAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to update document assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateDocumentAssessmentResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateDocumentAssessmentResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
